import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface SmsAyar {
    gondericiID: number;
    smsServisAktif: boolean;
    userCode: string;
    password: string;
    header: string;
    borcHatirlatmaAktif: boolean;
    borcHatirlatmaGunOnce: string;
    borcHatirlatmaGunSonra: string;
}

interface Gonderici {
    gondericiID: number;
    gondericiAdi: string;
}

const SmsAyarlariPage: React.FC = () => {
    const { t } = useTranslation();
    const [ayar, setAyar] = useState<SmsAyar>({
        gondericiID: 1,
        smsServisAktif: false,
        userCode: '',
        password: '',
        header: '',
        borcHatirlatmaAktif: false,
        borcHatirlatmaGunOnce: '',
        borcHatirlatmaGunSonra: '',
    });
    const [gondericiler, setGondericiler] = useState<Gonderici[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sifreGoster, setSifreGoster] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [ayarRes, gondericiRes] = await Promise.all([
                axiosInstance.get('/api/SmsAyarlari'),
                axiosInstance.get('/api/SmsAyarlari/gondericiler'),
            ]);
            const d = ayarRes.data;
            setAyar({
                gondericiID: d.gondericiID ?? 1,
                smsServisAktif: d.smsServisAktif ?? false,
                userCode: d.userCode ?? '',
                password: d.password ?? '',
                header: d.header ?? '',
                borcHatirlatmaAktif: d.borcHatirlatmaAktif ?? false,
                borcHatirlatmaGunOnce: d.borcHatirlatmaGunOnce?.toString() ?? '',
                borcHatirlatmaGunSonra: d.borcHatirlatmaGunSonra?.toString() ?? '',
            });
            setGondericiler(gondericiRes.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axiosInstance.post('/api/SmsAyarlari', {
                gondericiID: ayar.gondericiID,
                smsServisAktif: ayar.smsServisAktif,
                userCode: ayar.userCode || null,
                password: ayar.password || null,
                header: ayar.header || null,
                borcHatirlatmaAktif: ayar.borcHatirlatmaAktif,
                borcHatirlatmaGunOnce: ayar.borcHatirlatmaGunOnce ? parseInt(ayar.borcHatirlatmaGunOnce) : null,
                borcHatirlatmaGunSonra: ayar.borcHatirlatmaGunSonra ? parseInt(ayar.borcHatirlatmaGunSonra) : null,
            });
            toast.success(t('msg.islemBasarili'));
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const inputStyle: React.CSSProperties = {
        width: '100%', padding: '9px 13px', border: '1px solid #e2e8f0',
        borderRadius: 7, fontSize: 14, color: '#1e293b', background: '#fff',
        outline: 'none', boxSizing: 'border-box',
    };

    const labelStyle: React.CSSProperties = {
        display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6,
    };

    const sectionStyle: React.CSSProperties = {
        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
        padding: '20px 22px', marginBottom: 20,
    };

    const sectionTitleStyle: React.CSSProperties = {
        fontSize: 14, fontWeight: 700, color: '#1a2440', marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 8,
    };

    if (loading) return (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: 22 }}></i>
        </div>
    );

    return (
        <div style={{ maxWidth: 640, padding: '4px 0' }}>
            <form onSubmit={handleSubmit}>

                {/* Genel Ayarlar */}
                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>
                        <i className="fas fa-sms" style={{ color: '#e67d21' }}></i>
                        Genel SMS Ayarları
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <label style={labelStyle}>Gönderici Firma</label>
                        <select
                            value={ayar.gondericiID}
                            onChange={e => setAyar({ ...ayar, gondericiID: parseInt(e.target.value) })}
                            style={inputStyle}
                        >
                            {gondericiler.map(g => (
                                <option key={g.gondericiID} value={g.gondericiID}>{g.gondericiAdi}</option>
                            ))}
                        </select>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                        <div
                            onClick={() => setAyar({ ...ayar, smsServisAktif: !ayar.smsServisAktif })}
                            style={{
                                width: 42, height: 24, borderRadius: 12,
                                background: ayar.smsServisAktif ? '#22c55e' : '#d1d5db',
                                position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0,
                            }}
                        >
                            <div style={{
                                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                                position: 'absolute', top: 3,
                                left: ayar.smsServisAktif ? 21 : 3,
                                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            }} />
                        </div>
                        <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>SMS Servisi Aktif</span>
                    </label>
                </div>

                {/* API Bilgileri */}
                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>
                        <i className="fas fa-key" style={{ color: '#6366f1' }}></i>
                        API Bilgileri
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                        <div>
                            <label style={labelStyle}>User Code</label>
                            <input
                                type="text"
                                value={ayar.userCode}
                                onChange={e => setAyar({ ...ayar, userCode: e.target.value })}
                                placeholder={t('label.ara')}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>Password</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={sifreGoster ? 'text' : 'password'}
                                    value={ayar.password}
                                    onChange={e => setAyar({ ...ayar, password: e.target.value })}
                                    placeholder={t('label.ara')}
                                    style={{ ...inputStyle, paddingRight: 38 }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setSifreGoster(p => !p)}
                                    style={{
                                        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                                        background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0,
                                    }}
                                >
                                    <i className={`fas fa-${sifreGoster ? 'eye-slash' : 'eye'}`}></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label style={labelStyle}>Header</label>
                        <input
                            type="text"
                            value={ayar.header}
                            onChange={e => setAyar({ ...ayar, header: e.target.value })}
                            placeholder={t('label.ara')}
                            style={inputStyle}
                        />
                    </div>
                </div>

                {/* Borç Hatırlatma */}
                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>
                        <i className="fas fa-bell" style={{ color: '#f59e0b' }}></i>
                        Borç Hatırlatma
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', marginBottom: 18 }}>
                        <div
                            onClick={() => setAyar({ ...ayar, borcHatirlatmaAktif: !ayar.borcHatirlatmaAktif })}
                            style={{
                                width: 42, height: 24, borderRadius: 12,
                                background: ayar.borcHatirlatmaAktif ? '#22c55e' : '#d1d5db',
                                position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0,
                            }}
                        >
                            <div style={{
                                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                                position: 'absolute', top: 3,
                                left: ayar.borcHatirlatmaAktif ? 21 : 3,
                                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            }} />
                        </div>
                        <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Borç Hatırlatma Aktif</span>
                    </label>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, opacity: ayar.borcHatirlatmaAktif ? 1 : 0.45, pointerEvents: ayar.borcHatirlatmaAktif ? 'auto' : 'none' }}>
                        <div>
                            <label style={labelStyle}>
                                Ödenmeyen borçları&nbsp;
                                <span style={{ color: '#e67d21', fontWeight: 700 }}>
                                    {ayar.borcHatirlatmaGunOnce || '—'}
                                </span>
                                &nbsp;gün önce hatırlat
                            </label>
                            <input
                                type="number"
                                min={1}
                                value={ayar.borcHatirlatmaGunOnce}
                                onChange={e => setAyar({ ...ayar, borcHatirlatmaGunOnce: e.target.value })}
                                placeholder={t('label.ara')}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>
                                Ödenmeyen borçları&nbsp;
                                <span style={{ color: '#ef4444', fontWeight: 700 }}>
                                    {ayar.borcHatirlatmaGunSonra || '—'}
                                </span>
                                &nbsp;gün sonra hatırlat
                            </label>
                            <input
                                type="number"
                                min={1}
                                value={ayar.borcHatirlatmaGunSonra}
                                onChange={e => setAyar({ ...ayar, borcHatirlatmaGunSonra: e.target.value })}
                                placeholder={t('label.ara')}
                                style={inputStyle}
                            />
                        </div>
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={saving}
                    style={{
                        background: '#e67d21', color: '#fff', border: 'none',
                        borderRadius: 8, padding: '11px 28px', fontSize: 14, fontWeight: 600,
                        cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
                    }}
                >
                    {saving ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 7 }}></i>Kaydediliyor...</> : <><i className="fas fa-save" style={{ marginRight: 7 }}></i>Kaydet</>}
                </button>
            </form>
        </div>
    );
};

export default SmsAyarlariPage;
